import React, { useState } from 'react';
import { Truck } from 'lucide-react';
import { authService, notificationService } from '../services/apiService';
import { userStorage } from '../utils/storageUtils';
import { showLoginSuccessNotification } from '../utils/notificationDisplay';

const Login = ({ onLogin }) => {
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Hàm đăng ký FCM token với server
  const registerFCMTokenWithServer = async (userData) => {
    try {
      // Kiểm tra hỗ trợ FCM
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('FCM không được hỗ trợ trên thiết bị này');
        return;
      }

      // Import Firebase messaging
      const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
      const { initializeApp, getApps } = await import('firebase/app');

      // Kiểm tra hỗ trợ messaging
      const supported = await isSupported();
      if (!supported) {
        console.log('Firebase Messaging không được hỗ trợ');
        return;
      }

      // Cấu hình Firebase
      const firebaseConfig = {
        apiKey: "AIzaSyBCE9Hd2YKQn-xgEsObxq_y9eMMWH8HvE8",
        authDomain: "trongduan-16f32.firebaseapp.com",
        projectId: "trongduan-16f32",
        storageBucket: "trongduan-16f32.firebasestorage.app",
        messagingSenderId: "655745416209",
        appId: "1:655745416209:web:c60a7cbec954b0c6f105b0",
        measurementId: "G-5W1R2GBPXY"
      };

      const vapidKey = "BFxGATCXYofQaL3gYjd4GOlu0bZkCdKmZzWSJIAGhAaDRlkm96wFijjfq0VXM0no9yO6CKq6iFrCFNWGs6upJAo";

      // Khởi tạo Firebase app
      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
      const messaging = getMessaging(app);

      // Yêu cầu quyền thông báo
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Quyền thông báo bị từ chối');
        return;
      }

      // Lấy FCM token
      const token = await getToken(messaging, { vapidKey });
      if (!token) {
        console.log('Không thể lấy FCM token');
        return;
      }

      console.log('FCM Token nhận được:', token);

      // Đăng ký token với server
      const driverId = userData.idLaiXe;
      if (!driverId) {
        console.warn('Không tìm thấy ID lái xe để đăng ký FCM token');
        return;
      }

      console.log('Đăng ký FCM token với server cho lái xe ID:', driverId);

      const result = await notificationService.registerFCMToken(driverId, token);
      
      if (result.success) {
        console.log('Đăng ký FCM token thành công với server');
        // Hiển thị thông báo thành công
        showLoginSuccessNotification(userData.tenNhanSu || userData.userName || userName);
      } else {
        console.warn('Lỗi đăng ký FCM token với server:', result.message);
        // Vẫn hiển thị thông báo đăng nhập thành công dù FCM lỗi
        showLoginSuccessNotification(userData.tenNhanSu || userData.userName || userName);
      }

    } catch (error) {
      console.error('Lỗi trong quá trình đăng ký FCM token:', error);
      // Vẫn hiển thị thông báo đăng nhập thành công dù FCM lỗi
      showLoginSuccessNotification(userData.tenNhanSu || userData.userName || userName);
    }
  };

  const handleLogin = async () => {
    if (!userName || !password) {
      setError('Vui lòng nhập đầy đủ thông tin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await authService.login(userName, password);
      
      if (result.success && result.data && result.data.length > 0) {
        const userData = result.data[0];
        
        // Lưu thông tin người dùng
        userStorage.save(userData);
        
        // Đăng ký FCM token với server (async, không chặn UI)
        registerFCMTokenWithServer(userData).catch(err => {
          console.error('FCM registration failed but login continues:', err);
        });
        
        // Gọi callback login
        onLogin(userData);
      } else {
        setError(result.message || 'Đăng nhập thất bại');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Có lỗi xảy ra. Vui lòng thử lại');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full mb-4 shadow-lg border">
            <img src="https://blackmatrix2007.github.io/driver-pwa-app/td.png" alt="TD Logo" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Đăng Nhập</h1>
          <p className="text-gray-600 mt-2">Hệ thống quản lý vận chuyển</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tên đăng nhập
            </label>
            <input
              type="text"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nhập tên đăng nhập"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mật khẩu
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nhập mật khẩu"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
          </button>
        </div>

        {/* Debug info chỉ hiển thị trong development */}
        {/* {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-gray-100 rounded-lg text-xs text-gray-600">
            <p>🔧 Development Mode</p>
            <p>FCM token sẽ được đăng ký sau khi đăng nhập thành công</p>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default Login;